import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import AuthMethodSelector from './components/AuthMethodSelector';
import AuthForm from './components/AuthForm';
import OTPForm from './components/OTPForm';
import ForgotPasswordForm from './components/ForgotPasswordForm';
import AppImage from '../../components/AppImage';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import AdSense from '../../components/AdSense';
import { useAuth } from '../../contexts/AuthContext';

// Add toast mock function for demonstration
const toast = {
  success: (message) => {
    console.log('Success:', message);
    // In a real app, this would show a toast notification
  }
};

const AuthenticationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  
  const [currentView, setCurrentView] = useState('signin');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check URL parameters on mount
  useEffect(() => {
    const tab = searchParams?.get('tab');
    if (tab === 'register') {
      setCurrentView('signup');
    } else if (tab === 'signin') {
      setCurrentView('signin');
    }
  }, [searchParams]);

  // Redirect if user is already authenticated
  useEffect(() => {
    if (user && !authLoading) {
      const from = location?.state?.from?.pathname || '/progress-dashboard';
      navigate(from, { replace: true });
    }
  }, [user, authLoading, navigate, location]);

  // Handle authentication success
  const handleAuthSuccess = (user) => {
    console.log('Authentication successful:', user);
    
    // Show success message
    toast?.success(`Welcome ${user?.name || user?.email?.split('@')[0]}!`);
    
    // Immediate redirect to avoid any hanging
    try {
      const from = location?.state?.from?.pathname || '/progress-dashboard';
      console.log('Redirecting to:', from);
      navigate(from, { replace: true });
    } catch (navError) {
      console.error('Navigation error:', navError);
      // Fallback redirect
      window.location.href = '/progress-dashboard';
    }
  };

  const renderCurrentView = () => {
    // Always show the AuthForm for now to simplify
    return (
      <AuthForm
        mode={currentView}
        onModeChange={(mode) => {
          setCurrentView(mode);
          setError(null);
        }}
        onSuccess={handleAuthSuccess}
      />
    );
  };

  return (
    <>
      <Helmet>
        <title>Sign In - CareerLift</title>
        <meta name="description" content="Sign in to your CareerLift account to continue your career development journey with AI-powered coaching and personalized growth plans." />
      </Helmet>
      <div className="min-h-screen bg-background flex flex-col">
        <Header 
          user={user} 
          onAuthRequired={() => {}} // No-op since we're already on auth page
        />
        
        {/* AdSense - Banner for Free Users */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="w-full min-w-[300px]">
            <div className="text-xs text-muted-foreground text-center mb-1 opacity-60">
              Advertisement
            </div>
            <div className="block min-w-[300px] min-h-[50px] max-h-[80px] bg-gray-100 border border-gray-200 rounded flex items-center justify-center">
              <span className="text-gray-400 text-sm">Ad Space</span>
            </div>
          </div>
        </div>
        
        <main className="flex-1">
          <div className="flex items-center justify-center p-4 py-6">
            <div className="w-full max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                {/* Left Column - Branding & Benefits */}
                <div className="hidden lg:block space-y-6">
                  <div className="space-y-4 text-center">
                    <div className="flex justify-center">
                      <AppImage 
                        src="/assets/images/CareerLift_Logo_Vertical-1757447041740.png"
                        alt="CareerLift Logo"
                        className="h-16 w-auto"
                      />
                    </div>

                    <div className="space-y-3">
                      <h3 className="text-xl font-semibold text-foreground">
                        Accelerate Your Professional Growth
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Join thousands of professionals advancing their careers with AI coaching and personalized development plans.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right Column - Authentication Form */}
                <div className="w-full">
                  <div className="bg-card border border-border rounded-lg shadow-sm p-8">
                    {renderCurrentView()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        
        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default AuthenticationPage;